import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Switch, Select, InputNumber, Upload, Typography, Row, Col, Divider, Alert, Tabs, message as antdMessage } from 'antd';
import api from '../../../services/api';
import { SaveOutlined, UploadOutlined, MailOutlined, PhoneOutlined, GlobalOutlined } from '@ant-design/icons';
import { resolveImageSrc } from '../../../utils/images';
import soundNotificationService from '../../../services/soundNotification';

const { TabPane } = Tabs;
const { Title, Paragraph } = Typography;
const { TextArea } = Input;

function AdminSettings() {
    const [form] = Form.useForm();
    const [contactForm] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [emailForm] = Form.useForm();
    const [brandingForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleSave = async (values) => {
        setLoading(true);
        try {
            const entries = Object.entries(values);
            for (const [key, val] of entries) {
                let valueToStore = val;
                
                // Handle uploads: upload files to media library and store URL only
                if (key === 'logo' || key === 'favicon') {
                    // Handle both array format (from Upload component) and direct object format
                    let item = null;
                    if (Array.isArray(val) && val.length > 0) {
                        item = val[0];
                    } else if (val && typeof val === 'object' && val.originFileObj) {
                        item = val;
                    }
                    
                    if (item?.originFileObj) {
                        // New file upload
                        const formData = new FormData();
                        formData.append('file', item.originFileObj);
                        formData.append('category', key === 'logo' ? 'branding-logo' : 'branding-favicon');
                        formData.append('alt_text', key);
                        formData.append('description', `${key} for platform branding`);
                        formData.append('is_public', '1');
                        
                        try {
                            const uploadRes = await api.adminUploadMedia(formData);
                            valueToStore = uploadRes.data?.url || uploadRes.url || null;
                        } catch (error) {
                            console.error(`Failed to upload ${key}:`, error);
                            antdMessage.error(`Failed to upload ${key}`);
                            continue;
                        }
                    } else if (item?.url) {
                        // Existing file URL
                        valueToStore = item.url;
                    } else if (typeof val === 'string' && (val.startsWith('/storage/') || val.startsWith('http'))) {
                        // Already a URL, keep as is
                        valueToStore = val;
                    } else {
                        // Skip if no valid file or URL
                        continue;
                    }
                }
                
                const payload = {
                    key: `settings_${key}`,
                    type: 'json',
                    content: JSON.stringify({ value: valueToStore }),
                    section: 'settings',
                    is_active: true,
                };
                await api.adminCreateContent(payload);
            }
            setMessage('Settings saved successfully!');
            antdMessage.success('Settings saved');
            setTimeout(() => setMessage(''), 3000);
            await fetchSettings();
        } catch (error) {
            console.error('Error saving settings:', error);
            antdMessage.error('Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const res = await api.adminGetContent({ section: 'settings' });
            const items = res.data?.data || res.data || [];
            const map = {};
            items.forEach((item) => {
                if (!item.key?.startsWith('settings_')) return;
                const k = item.key.replace('settings_', '');
                let v = undefined;
                try {
                    const parsed = JSON.parse(item.content || 'null');
                    v = parsed && typeof parsed === 'object' ? parsed.value : item.content;
                } catch (e) {
                    v = item.content;
                }
                map[k] = v;
            });
            
            // Handle logo and favicon for upload components
            if (map.logo) {
                map.logo = [{
                    uid: '-1',
                    name: 'logo.png',
                    status: 'done',
                    url: resolveImageSrc(map.logo),
                }];
            }
            if (map.favicon) {
                map.favicon = [{
                    uid: '-2',
                    name: 'favicon.ico',
                    status: 'done',
                    url: resolveImageSrc(map.favicon),
                }];
            }
            
            if (Object.keys(map).length) {
                form.setFieldsValue(map);
                contactForm.setFieldsValue(map);
                paymentForm.setFieldsValue(map);
                emailForm.setFieldsValue(map);
                brandingForm.setFieldsValue(map);
            }
        } catch (e) {
            console.error('Error fetching settings:', e);
        }
    };

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto p-6">
                <div className="mb-6">
                    <Title level={2}>Platform Settings</Title>
                    <Paragraph type="secondary">
                        Configure platform-wide settings and preferences
                    </Paragraph>
                </div>

                {message && (
                    <Alert
                        message={message}
                        type="success"
                        className="mb-6"
                        closable
                    />
                )}

                <Card>
                    <Tabs defaultActiveKey="general">
                        <TabPane tab="General" key="general">
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleSave}
                                initialValues={{}}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="platform_name"
                                            label="Platform Name"
                                            rules={[{ required: true, message: 'Please enter platform name' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="platform_tagline"
                                            label="Platform Tagline"
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item
                                            name="default_currency"
                                            label="Default Currency"
                                        >
                                            <Select>
                                                <Select.Option value="INR">INR (₹)</Select.Option>
                                                <Select.Option value="USD">USD ($)</Select.Option>
                                                <Select.Option value="EUR">EUR (€)</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="timezone"
                                            label="Timezone"
                                        >
                                            <Select>
                                                <Select.Option value="Asia/Kolkata">Asia/Kolkata</Select.Option>
                                                <Select.Option value="Asia/Dubai">Asia/Dubai</Select.Option>
                                                <Select.Option value="America/New_York">America/New_York</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="language"
                                            label="Default Language"
                                        >
                                            <Select>
                                                <Select.Option value="en">English</Select.Option>
                                                <Select.Option value="hi">Hindi</Select.Option>
                                                <Select.Option value="bn">Bengali</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider />

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="maintenance_mode"
                                            label="Maintenance Mode"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="registration_enabled"
                                            label="User Registration"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider />

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="Enable Sound Notifications">
                                            <Switch 
                                                defaultChecked={soundNotificationService.isSoundEnabled()} 
                                                onChange={(v) => {
                                                    try { soundNotificationService.setEnabled(v); } catch(_) {}
                                                    antdMessage.success(`Sound notifications ${v ? 'enabled' : 'disabled'}`);
                                                }} 
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Enable Ringtone for Critical Events">
                                            <Switch 
                                                defaultChecked={soundNotificationService.isRingtoneEnabled()} 
                                                onChange={(v) => {
                                                    try { soundNotificationService.setRingtoneEnabled(v); } catch(_) {}
                                                    antdMessage.success(`Ringtone ${v ? 'enabled' : 'disabled'}`);
                                                }} 
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="vendor_registration_enabled"
                                            label="Vendor Registration"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="email_verification_required"
                                            label="Email Verification Required"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Save General Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </TabPane>

                        <TabPane tab="Contact & Support" key="contact">
                            <Form
                                form={contactForm}
                                layout="vertical"
                                onFinish={handleSave}
                                initialValues={{}}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="support_email"
                                            label="Support Email"
                                            rules={[
                                                { required: true, message: 'Please enter support email' },
                                                { type: 'email', message: 'Please enter valid email' }
                                            ]}
                                        >
                                            <Input prefix={<MailOutlined />} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="admin_email"
                                            label="Admin Email"
                                            rules={[
                                                { required: true, message: 'Please enter admin email' },
                                                { type: 'email', message: 'Please enter valid email' }
                                            ]}
                                        >
                                            <Input prefix={<MailOutlined />} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="support_phone"
                                            label="Support Phone"
                                        >
                                            <Input prefix={<PhoneOutlined />} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="business_hours"
                                            label="Business Hours"
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    name="support_address"
                                    label="Business Address"
                                >
                                    <TextArea rows={3} />
                                </Form.Item>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Save Contact Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </TabPane>

                        <TabPane tab="Payment & Fees" key="payment">
                            <Form
                                form={paymentForm}
                                layout="vertical"
                                onFinish={handleSave}
                                initialValues={{}}
                            >
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item
                                            name="platform_commission"
                                            label="Platform Commission (%)"
                                        >
                                            <InputNumber min={0} max={50} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="tax_rate"
                                            label="Tax Rate (%)"
                                        >
                                            <InputNumber min={0} max={30} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="delivery_fee"
                                            label="Default Delivery Fee (₹)"
                                        >
                                            <InputNumber min={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="payment_gateway"
                                            label="Payment Gateway"
                                        >
                                            <Select>
                                                <Select.Option value="razorpay">Razorpay</Select.Option>
                                                <Select.Option value="stripe">Stripe</Select.Option>
                                                <Select.Option value="payu">PayU</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="min_order_amount"
                                            label="Minimum Order Amount (₹)"
                                        >
                                            <InputNumber min={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Save Payment Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </TabPane>

                        <TabPane tab="Email Templates" key="email">
                            <Form
                                form={emailForm}
                                layout="vertical"
                                onFinish={handleSave}
                                initialValues={{}}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="welcome_email_subject"
                                            label="Welcome Email Subject"
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="booking_confirmation_subject"
                                            label="Booking Confirmation Subject"
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    name="vendor_approval_subject"
                                    label="Vendor Approval Subject"
                                >
                                    <Input />
                                </Form.Item>

                                <Form.Item
                                    name="welcome_email_template"
                                    label="Welcome Email Template"
                                >
                                    <TextArea rows={8} placeholder="Dear {{user_name}}, Welcome to Spoon & Spice..." />
                                </Form.Item>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Save Email Templates
                                    </Button>
                                </Form.Item>
                            </Form>
                        </TabPane>

                        <TabPane tab="Branding" key="branding">
                            <Form
                                form={brandingForm}
                                layout="vertical"
                                onFinish={handleSave}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="logo"
                                            label="Platform Logo"
                                            valuePropName="fileList"
                                            getValueFromEvent={(e) => {
                                                if (Array.isArray(e)) {
                                                    return e;
                                                }
                                                return e && e.fileList;
                                            }}
                                        >
                                            <Upload
                                                listType="picture-card"
                                                maxCount={1}
                                                beforeUpload={() => false}
                                                accept="image/*"
                                                showUploadList={{
                                                    showPreviewIcon: true,
                                                    showRemoveIcon: true,
                                                }}
                                            >
                                                <div>
                                                    <UploadOutlined />
                                                    <div style={{ marginTop: 8 }}>Upload Logo</div>
                                                </div>
                                            </Upload>
                                        </Form.Item>
                                        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                            Recommended size: 200x60px. Max file size: 30MB
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="favicon"
                                            label="Favicon"
                                            valuePropName="fileList"
                                            getValueFromEvent={(e) => {
                                                if (Array.isArray(e)) {
                                                    return e;
                                                }
                                                return e && e.fileList;
                                            }}
                                        >
                                            <Upload
                                                listType="picture-card"
                                                maxCount={1}
                                                beforeUpload={() => false}
                                                accept="image/*"
                                                showUploadList={{
                                                    showPreviewIcon: true,
                                                    showRemoveIcon: true,
                                                }}
                                            >
                                                <div>
                                                    <UploadOutlined />
                                                    <div style={{ marginTop: 8 }}>Upload Favicon</div>
                                                </div>
                                            </Upload>
                                        </Form.Item>
                                        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                            Recommended size: 32x32px. Max file size: 30MB
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item
                                            name="primary_color"
                                            label="Primary Color"
                                        >
                                            <Input type="color" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="secondary_color"
                                            label="Secondary Color"
                                        >
                                            <Input type="color" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="accent_color"
                                            label="Accent Color"
                                        >
                                            <Input type="color" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Save Branding Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </TabPane>
                    </Tabs>
                </Card>
            </div>
        </div>
    );
}

export default AdminSettings;
